import { addCommas, round } from './helpers';
import { GoalFormat, resolveGoalFormat } from './kpiOptions';

// kpiPerformance.ts — turns the raw daily rows from the contract KPI-performance
// endpoint (ADOPS-2696, GET /api/admin/v2/contracts/kpi_performance) into the
// per-KPI display model the KpiPerformanceChips + Risk column consume. This
// aggregation is net-new (the prototype consumed pre-computed values); it is the
// front-end half of the daily-vs-cumulative rule the ticket describes.

export type TemporalBasis = 'daily' | 'cumulative';

// One raw row as returned by the backend (grain: contract × kpi_name × report_day).
// The metric floats are nullable — a lot of KPIs never receive performance data.
export interface KpiPoint {
  kpi_name: string;
  goal_value: number | null;
  temporal_basis: TemporalBasis;
  report_day: string; // YYYY-MM-DD
  metric_numerator: number | null;
  metric_denominator: number | null;
  performance: number | null;
  provider?: string | null; // metric source (e.g. DoubleClick / DV / IAS); null/absent when unset
}

// The aggregated, display-ready KPI for one contract.
export interface KpiPerformance {
  name: string; // raw kpi_name (also the chip label)
  goal: number | null;
  value: number | null; // aggregated total performance
  pctOfGoal: number | null; // value / goal * 100 (null when no data)
  format: GoalFormat;
  temporal: TemporalBasis;
  hasData: boolean;
  provider?: string | null; // metric source, null/absent when the backend leaves it unset
  // Absolute per-day performance for the last 5 days, oldest→newest. Only set for
  // daily KPIs (cumulative KPIs show no sparkline — a daily breakdown of a
  // periodically-computed study would be misleading).
  dailyValues?: number[];
}

const SPARKLINE_DAYS = 5;

// perDayPerformance derives a single day's performance ratio: prefer the
// backend's own `performance`, else numerator/denominator when both are present.
function perDayPerformance(p: KpiPoint): number | null {
  if (p.performance != null) return p.performance;
  if (p.metric_numerator != null && p.metric_denominator != null && p.metric_denominator !== 0) {
    return p.metric_numerator / p.metric_denominator;
  }
  return null;
}

function firstNonNull<T>(values: (T | null | undefined)[]): T | null {
  const hit = values.find((v) => v != null);
  return hit == null ? null : hit;
}

// dedupeByDay collapses rows that share a report_day. The backend can emit a day
// twice (a month-boundary partition artifact — see the prod fixture); the rows
// are the same measurement, so we keep the last one. Both the daily total and
// the sparkline then operate on genuine per-day rows.
function dedupeByDay(rows: KpiPoint[]): KpiPoint[] {
  const byDay = new Map<string, KpiPoint>();
  rows.forEach((r) => byDay.set(r.report_day, r));
  return Array.from(byDay.values());
}

// aggregateOneKpi folds one KPI's daily rows (already grouped, unsorted) into a
// single KpiPerformance. Daily KPIs total as sum(numerator)/sum(denominator)
// ("summing first"); cumulative KPIs use the most recent `performance`.
function aggregateOneKpi(name: string, rows: KpiPoint[], format: GoalFormat): KpiPerformance {
  const sorted = dedupeByDay(rows).sort((a, b) => a.report_day.localeCompare(b.report_day));
  const temporal: TemporalBasis = sorted[0]?.temporal_basis ?? 'daily';
  const goal = firstNonNull(sorted.map((r) => r.goal_value));
  // Provider is constant per kpi_name; take the first the rows report.
  const provider = firstNonNull(sorted.map((r) => r.provider));

  let value: number | null;
  let dailyValues: number[] | undefined;

  if (temporal === 'daily') {
    let sumNum = 0;
    let sumDen = 0;
    let sawRatio = false;
    sorted.forEach((r) => {
      if (r.metric_numerator != null && r.metric_denominator != null) {
        sumNum += r.metric_numerator;
        sumDen += r.metric_denominator;
        sawRatio = true;
      }
    });
    if (sawRatio && sumDen !== 0) {
      value = sumNum / sumDen;
    } else {
      // No usable numerator/denominator — fall back to the latest daily performance.
      value = firstNonNull(sorted.map((r) => r.performance).reverse());
    }

    const series = sorted
      .map(perDayPerformance)
      .filter((v): v is number => v != null)
      .slice(-SPARKLINE_DAYS);
    dailyValues = series.length > 0 ? series : undefined;
  } else {
    // Cumulative: the most recent performance value is the total; no sparkline.
    value = firstNonNull(sorted.map((r) => r.performance).reverse());
  }

  const hasData = value != null && goal != null && goal !== 0;
  const pctOfGoal = hasData ? (value! / goal!) * 100 : null;

  return { name, goal, value, pctOfGoal, format, temporal, hasData, provider, dailyValues };
}

// aggregateContractKpis groups one contract's raw rows by kpi_name and aggregates
// each. goalFormats maps a KPI name to its display format (from the reused
// kpi_options catalog); resolveGoalFormat applies the per-KPI overrides and the
// plain-number fallback for names the catalog doesn't carry.
export function aggregateContractKpis(
  points: KpiPoint[],
  goalFormats: Record<string, GoalFormat> = {}
): KpiPerformance[] {
  const byName = new Map<string, KpiPoint[]>();
  points.forEach((p) => {
    const group = byName.get(p.kpi_name);
    if (group) {
      group.push(p);
    } else {
      byName.set(p.kpi_name, [p]);
    }
  });

  return Array.from(byName.entries()).map(([name, rows]) => aggregateOneKpi(name, rows, resolveGoalFormat(name, goalFormats)));
}

// Classification (ticket: on-track = "performing at or above goal"). No
// higher-vs-lower-better direction exists in the data, so every KPI is treated as
// higher-is-better; see the PR notes.
export const isOnTrack = (k: KpiPerformance): boolean => k.hasData && (k.pctOfGoal as number) >= 100;
export const isOffTrack = (k: KpiPerformance): boolean => k.hasData && (k.pctOfGoal as number) < 100;
export const isNoData = (k: KpiPerformance): boolean => !k.hasData;

// riskCount is the "Risk" column value: how many of a contract's KPIs are
// off-track (below goal). KPIs with no value or no goal are no-data, not risk.
export const riskCount = (kpis: KpiPerformance[]): number => kpis.filter(isOffTrack).length;

// formatKpiValue renders a KPI value/goal for display, driven by the kpi_options
// goal format. Reuses the shared currency helpers (round/addCommas) rather than
// re-implementing number formatting.
export function formatKpiValue(value: number | null, format: GoalFormat): string {
  if (value == null || Number.isNaN(value)) return '—';
  switch (format) {
    case 'percent': {
      // Backend stores percent KPIs as ratios (CTR goal 0.002, Viewability 0.8),
      // so scale to percent. Keep 2 decimals for sub-1% values (e.g. CTR), 1 otherwise.
      const pct = value * 100;
      return `${Math.abs(pct) < 1 ? pct.toFixed(2) : pct.toFixed(1)}%`;
    }
    case 'dollars':
      return `$${round(value)}`;
    default:
      return String(addCommas(round(value)));
  }
}
